import { zodResolver } from '@hookform/resolvers/zod';
import {
  priceAdjustmentModeSchema,
  priceAdjustmentTargetSchema,
  productBulkPriceUpdateSchema,
  type ProductBulkPriceUpdateInput,
} from '@erp/shared';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FormField } from '@/components/common/form-field';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useBulkPriceUpdate } from '@/features/products/api/use-products';
import { numberOrDefault } from '@/features/products/lib/form-value';

type FormValues = ProductBulkPriceUpdateInput;

export interface BulkPriceDialogProps {
  productIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied: () => void;
}

export function BulkPriceDialog({
  productIds,
  open,
  onOpenChange,
  onApplied,
}: BulkPriceDialogProps): JSX.Element {
  const { t } = useTranslation();
  const bulkUpdate = useBulkPriceUpdate();

  const form = useForm<FormValues>({
    resolver: zodResolver(productBulkPriceUpdateSchema),
    defaultValues: {
      productIds,
      target: 'LIST_PRICE',
      mode: 'PERCENT',
      value: '0',
      roundTo: 2,
      reason: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        productIds,
        target: 'LIST_PRICE',
        mode: 'PERCENT',
        value: '0',
        roundTo: 2,
        reason: '',
      });
    }
  }, [open, productIds, form]);

  const onSubmit = form.handleSubmit((values) => {
    bulkUpdate.mutate(
      { ...values, productIds },
      {
        onSuccess: (result) => {
          toast.success(
            t('products.bulkPrice.success', {
              updated: result.updated,
              skipped: result.skipped.length,
            }),
          );
          onOpenChange(false);
          onApplied();
        },
        onError: (error) =>
          toast.error(t('products.bulkPrice.error'), { description: error.message }),
      },
    );
  });

  const errors = form.formState.errors;
  const mode = form.watch('mode');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('products.bulkPrice.title')}</DialogTitle>
          <DialogDescription>
            {t('products.bulkPrice.description', { count: productIds.length })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <FormField id="bulk-target" label={t('products.bulkPrice.fields.target')} required>
            <Select
              value={form.watch('target') ?? 'LIST_PRICE'}
              onValueChange={(value) =>
                form.setValue('target', value as NonNullable<FormValues['target']>, {
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger id="bulk-target">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {priceAdjustmentTargetSchema.options.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`products.bulkPrice.targets.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField id="bulk-mode" label={t('products.bulkPrice.fields.mode')} required>
            <Select
              value={mode}
              onValueChange={(value) =>
                form.setValue('mode', value as FormValues['mode'], { shouldDirty: true })
              }
            >
              <SelectTrigger id="bulk-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {priceAdjustmentModeSchema.options.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`products.bulkPrice.modes.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          <FormField
            id="bulk-value"
            label={t(`products.bulkPrice.valueLabels.${mode}`)}
            required
            error={errors.value?.message}
          >
            <Input
              id="bulk-value"
              className="text-right tabular-nums"
              {...form.register('value')}
            />
          </FormField>

          <FormField
            id="bulk-round"
            label={t('products.bulkPrice.fields.roundTo')}
            error={errors.roundTo?.message}
          >
            <Input
              id="bulk-round"
              type="number"
              min={0}
              max={4}
              className="text-right tabular-nums"
              {...form.register('roundTo', {
                setValueAs: (value: unknown) => numberOrDefault(value, 2),
              })}
            />
          </FormField>

          <FormField
            id="bulk-reason"
            label={t('products.bulkPrice.fields.reason')}
            description={t('products.bulkPrice.fields.reasonHint')}
            required
            error={errors.reason?.message}
          >
            <Textarea id="bulk-reason" rows={3} {...form.register('reason')} />
          </FormField>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={bulkUpdate.isPending || productIds.length === 0}>
              {bulkUpdate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              {t('products.bulkPrice.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
